import { useEffect, useState } from 'react';

type HeldItem = { expireAt?: string | null };

export type BasketHoldState = 'none' | 'holding' | 'expired';

export type BasketHold = {
  state: BasketHoldState;
  msRemaining: number | null;
  expiresAt: Date | null;
};

const TICK_MS = 1000;

function earliestExpiry(items: HeldItem[] | undefined | null): number | null {
  let earliest: number | null = null;

  for (const item of Array.isArray(items) ? items : []) {
    if (!item.expireAt) continue;

    const at = Date.parse(item.expireAt);
    if (Number.isNaN(at)) continue;

    if (earliest === null || at < earliest) earliest = at;
  }

  return earliest;
}

export function useBasketHold(
  items: HeldItem[] | undefined | null,
  tickWithinMs = Infinity
): BasketHold {
  const earliest = earliestExpiry(items);
  const [, setTick] = useState(0);

  const msRemaining = earliest === null ? null : Math.max(0, earliest - Date.now());
  const isTicking = msRemaining !== null && msRemaining > 0 && msRemaining <= tickWithinMs;

  useEffect(() => {
    const bump = () => setTick((n) => n + 1);

    if (earliest === null || earliest <= Date.now()) return undefined;

    if (!isTicking) {
      const wake = setTimeout(bump, Math.max(0, earliest - tickWithinMs - Date.now()));
      return () => clearTimeout(wake);
    }

    const id = setInterval(() => {
      bump();
      if (Date.now() >= earliest) clearInterval(id);
    }, TICK_MS);

    return () => clearInterval(id);
  }, [earliest, tickWithinMs, isTicking]);

  if (earliest === null || msRemaining === null) {
    return { state: 'none', msRemaining: null, expiresAt: null };
  }

  return {
    state: msRemaining === 0 ? 'expired' : 'holding',
    msRemaining,
    expiresAt: new Date(earliest),
  };
}
